import { Routes, Route } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import Layout from '@/components/Layout';
import ErrorBoundary from '@/components/ErrorBoundary';
import NotFound from '@/components/NotFound';

import Dashboard from '@/features/dashboard/Dashboard';
import Login from '@/features/auth/Login';
import Register from '@/features/auth/Register';
import ForgotPassword from '@/features/auth/ForgotPassword';
import ResetPassword from '@/features/auth/ResetPassword';
import VerifyEmail from '@/features/auth/VerifyEmail';
import ResendVerification from '@/features/auth/ResendVerification';
import Channels from '@/features/channels/Channels';
import ChannelDetail from '@/features/channels/components/ChannelDetail';
import Discover from '@/features/discover/Discover';
import Videos from '@/features/videos/Videos';
import ArtistPage from '@/features/artists/ArtistPage';
import AlbumPage from '@/features/albums/AlbumPage';
import Collections from '@/features/collections/Collections';
import Crates from '@/features/crates/Crates';
import CrateDetail from '@/features/crates/CrateDetail';
import Sets from '@/features/sets/Sets';
import SetBuilder from '@/features/sets/SetBuilder';
import Decks from '@/features/decks/Decks';

import { AuthProvider } from '@/features/auth/AuthContext';
import { SocketProvider } from '@/services/socket/SocketContext';
import { PlayerProvider } from '@/features/player/PlayerProvider';
import { ThemeProvider } from '@/contexts/ThemeContext';

import ProtectedRoute from '@/components/ProtectedRoute';

import { createQueryClient } from '@/lib/queryClient';

const queryClient = createQueryClient();

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider defaultTheme="dark">
          <AuthProvider>
            <SocketProvider>
              <PlayerProvider>
                <div className="min-h-screen bg-meradio-cream dark:bg-bg-dark-primary">
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/resend-verification" element={<ResendVerification />} />

            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Dashboard />} />
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/channels" element={<Channels />} />
                      <Route path="/channels/:channelId" element={<ChannelDetail />} />
                      <Route path="/discover" element={<Discover />} />
                      <Route path="/videos" element={<Videos />} />
                      <Route path="/artists/:name" element={<ArtistPage />} />
                      <Route path="/albums/:name" element={<AlbumPage />} />
                      <Route path="/collections" element={<Collections />} />
                      <Route path="/crates" element={<Crates />} />
                      <Route path="/crates/:id" element={<CrateDetail />} />
                      <Route path="/sets" element={<Sets />} />
                      <Route path="/sets/:id" element={<SetBuilder />} />
                      <Route path="/decks" element={<Decks />} />

                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Layout>
                </ProtectedRoute>
              }
            />
          </Routes>
            </div>

            {process.env.NODE_ENV === 'development' && (
              <ReactQueryDevtools initialIsOpen={false} />
            )}
              </PlayerProvider>
            </SocketProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
